import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function DominiosPage() {
  const [searchDomain, setSearchDomain] = useState('');
  const [searchResult, setSearchResult] = useState<'available' | 'taken' | null>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchDomain.trim()) {
      // Simulación de búsqueda
      const isAvailable = Math.random() > 0.5;
      setSearchResult(isAvailable ? 'available' : 'taken');
    }
  };

  const extensions = [
    { ext: '.cl', price: 12990, popular: true, description: 'Dominio chileno' },
    { ext: '.com', price: 14990, popular: true, description: 'El más popular' },
    { ext: '.net', price: 15990, popular: false, description: 'Para redes' },
    { ext: '.org', price: 15990, popular: false, description: 'Organizaciones' },
    { ext: '.io', price: 39990, popular: false, description: 'Tech startups' },
    { ext: '.app', price: 19990, popular: false, description: 'Aplicaciones' },
    { ext: '.dev', price: 19990, popular: false, description: 'Desarrolladores' },
    { ext: '.store', price: 24990, popular: false, description: 'Tiendas online' },
    { ext: '.tech', price: 24990, popular: false, description: 'Tecnología' },
    { ext: '.online', price: 19990, popular: false, description: 'Presencia web' },
    { ext: '.site', price: 16990, popular: false, description: 'Sitios web' },
    { ext: '.shop', price: 29990, popular: false, description: 'E-commerce' }
  ];

  const pricingTable = [
    { extension: '.cl', registro: 12990, renovacion: 12990, transferencia: 12990 },
    { extension: '.com', registro: 14990, renovacion: 14990, transferencia: 14990 },
    { extension: '.net', registro: 15990, renovacion: 15990, transferencia: 15990 },
    { extension: '.org', registro: 15990, renovacion: 15990, transferencia: 15990 },
    { extension: '.io', registro: 39990, renovacion: 39990, transferencia: 39990 },
    { extension: '.app', registro: 19990, renovacion: 19990, transferencia: 19990 },
    { extension: '.dev', registro: 19990, renovacion: 19990, transferencia: 19990 },
    { extension: '.store', registro: 24990, renovacion: 24990, transferencia: 24990 }
  ];

  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: 'Privacidad WHOIS',
      description: 'Protege tus datos personales con privacidad WHOIS incluida gratis en dominios elegibles'
    },
    {
      icon: 'ri-global-line',
      title: 'DNS Gratis',
      description: 'Gestión de DNS completa y gratuita con panel de control intuitivo'
    },
    {
      icon: 'ri-lock-line',
      title: 'Bloqueo de Transferencia',
      description: 'Protección contra transferencias no autorizadas de tu dominio'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Forwarding',
      description: 'Redirección de correos ilimitada incluida con tu dominio'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Auto-renovación',
      description: 'Nunca pierdas tu dominio con renovación automática opcional'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Soporte Experto',
      description: 'Equipo de soporte disponible 24/7 para ayudarte con tu dominio'
    }
  ];

  const faqs = [
    {
      question: '¿Qué es un nombre de dominio?',
      answer: 'Un nombre de dominio es la dirección única de tu sitio web en internet (ejemplo: tuempresa.cl). Es lo que las personas escriben en el navegador para visitar tu sitio.'
    },
    {
      question: '¿Cuánto tiempo tarda en activarse un dominio?',
      answer: 'Los dominios .com, .net y .org se activan instantáneamente. Los dominios .cl pueden tardar entre 24-48 horas en propagarse completamente.'
    },
    {
      question: '¿Puedo transferir mi dominio desde otro proveedor?',
      answer: 'Sí, puedes transferir tu dominio fácilmente. Solo necesitas el código de autorización (EPP) de tu proveedor actual y que el dominio tenga más de 60 días de antigüedad.'
    },
    {
      question: '¿Incluye hosting el registro de dominio?',
      answer: 'El dominio y el hosting son servicios separados. Sin embargo, ofrecemos paquetes combinados con descuento si contratas ambos servicios juntos.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Registro de Dominios .CL y DNS Personalizado | Gestión Profesional en Chile</title>
        <meta name="description" content="Registro de dominios .CL, .COM y extensiones internacionales con DNS personalizado y gestión profesional. Protección WHOIS, transferencias gratuitas y configuración DNS avanzada en Santiago." />
        <meta name="keywords" content="dominios chile, registro dominio cl, DNS chile, dominio .cl, transferencia dominios, DNS personalizado, gestión dominios chile, dominio profesional" />
        <link rel="canonical" href="https://www.ehostingchile.com/dominios" />
        <meta property="og:title" content="Registro de Dominios .CL y DNS Personalizado | Gestión Profesional en Chile" />
        <meta property="og:description" content="Registro de dominios .CL, .COM y extensiones internacionales con DNS personalizado y gestión profesional." />
        <meta property="og:url" content="https://www.ehostingchile.com/dominios" />
      </Helmet>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-700 text-white py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-300 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h1 className="text-5xl font-bold mb-6">
                Encuentra tu Dominio Perfecto
              </h1>
              <p className="text-xl text-cyan-100 mb-8">
                Registra tu nombre de dominio y comienza a construir tu presencia online. 
                Dominios .cl, .com y más de 100 extensiones disponibles.
              </p>
            </div>

            {/* Domain Search */}
            <div className="max-w-3xl mx-auto">
              <form onSubmit={handleSearch} className="relative">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={searchDomain}
                      onChange={(e) => setSearchDomain(e.target.value)}
                      placeholder="Busca tu dominio ideal (ej: tuempresa.cl)"
                      className="w-full px-6 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    />
                    {searchResult && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl p-4 text-gray-900">
                        {searchResult === 'available' ? (
                          <div className="flex items-center gap-3">
                            <i className="ri-check-line text-2xl text-green-600"></i>
                            <div>
                              <p className="font-semibold text-green-600">¡Disponible!</p>
                              <p className="text-sm text-gray-600">{searchDomain} está disponible para registro</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <i className="ri-close-line text-2xl text-red-600"></i>
                            <div>
                              <p className="font-semibold text-red-600">No disponible</p>
                              <p className="text-sm text-gray-600">Prueba con otra extensión o nombre</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Buscar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Popular Extensions */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Extensiones de Dominio Disponibles
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Elige entre más de 100 extensiones de dominio para tu proyecto
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {extensions.map((ext, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all hover:-translate-y-1 ${
                    ext.popular ? 'ring-2 ring-teal-600' : ''
                  }`}
                >
                  {ext.popular && (
                    <div className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3 whitespace-nowrap">
                      Popular
                    </div>
                  )}
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {ext.ext}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{ext.description}</p>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-2xl font-bold text-teal-600">
                      ${ext.price.toLocaleString('es-CL')}
                    </span>
                    <span className="text-gray-600 text-sm">/año</span>
                  </div>
                  <Link
                    to="/contratar"
                    className="block w-full text-center py-2 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Registrar
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Table */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Tabla de Precios Completa
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Precios transparentes para registro, renovación y transferencia de dominios
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white shadow-lg rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                    <th className="text-left py-4 px-6 font-semibold">
                      Extensión
                    </th>
                    <th className="text-center py-4 px-6 font-semibold">
                      Registro
                    </th>
                    <th className="text-center py-4 px-6 font-semibold">
                      Renovación
                    </th>
                    <th className="text-center py-4 px-6 font-semibold">
                      Transferencia
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pricingTable.map((row, index) => (
                    <tr
                      key={index}
                      className={`border-b border-gray-100 hover:bg-gray-50 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <td className="py-4 px-6 font-semibold text-gray-900">
                        {row.extension}
                      </td>
                      <td className="py-4 px-6 text-center text-gray-700">
                        ${row.registro.toLocaleString('es-CL')}/año
                      </td>
                      <td className="py-4 px-6 text-center text-gray-700">
                        ${row.renovacion.toLocaleString('es-CL')}/año
                      </td>
                      <td className="py-4 px-6 text-center text-gray-700">
                        ${row.transferencia.toLocaleString('es-CL')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                * Precios en pesos chilenos (CLP). IVA incluido.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Beneficios Incluidos
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Todo lo que necesitas para gestionar tu dominio de forma segura y profesional
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${benefit.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Preguntas Frecuentes
              </h2>
              <p className="text-gray-600">
                Respuestas a las dudas más comunes sobre dominios
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors cursor-pointer group"
                >
                  <summary className="flex items-center justify-between font-semibold text-gray-900 cursor-pointer">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-xl text-gray-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              ¿Listo para Registrar tu Dominio?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Asegura tu nombre de dominio hoy y comienza a construir tu marca online
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contratar"
                className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Registrar Dominio
              </Link>
              <Link
                to="/soporte"
                className="bg-teal-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                Consultar Disponibilidad
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}